import {
  Button,
  CameraCapture,
  cameraAvailable,
  Input,
  PasteButton,
  IconButton,
} from '@hue/design-system';
import { Camera, ClipboardPaste, FileText, ImageIcon, Link2, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  describePending,
  nextPhotoName,
  pasteShortcut,
  readImagesFromClipboard,
  type PendingReceipt,
} from '../../app/receipt-staging.js';

const ACCEPTED = 'application/pdf,image/jpeg,image/png,image/webp';

/// Escolher arquivo, arrastar, colar ou colar um link. Serve tanto ao cadastro, onde os anexos
/// esperam o lançamento nascer, quanto à edição, onde sobem na hora.
export function ReceiptPicker({
  pending,
  onAdd,
  onRemove,
  disabled = false,
  label = 'Comprovantes',
}: {
  pending: PendingReceipt[];
  onAdd: (items: PendingReceipt[]) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string>();
  const [pasteError, setPasteError] = useState<string>();
  const [cameraOpen, setCameraOpen] = useState(false);

  /// Colar vale em qualquer ponto do formulário, não só dentro da caixa: ninguém clica na caixa
  /// antes de dar Ctrl+V.
  useEffect(() => {
    if (disabled) return undefined;
    const onPaste = (event: globalThis.ClipboardEvent): void => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length === 0) return;
      event.preventDefault();
      onAdd(files.map((file) => ({ kind: 'file', file })));
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  });

  function addFiles(files: FileList | File[] | null) {
    const list = Array.from(files ?? []);
    if (list.length > 0) onAdd(list.map((file) => ({ kind: 'file', file })));
    if (inputRef.current) inputRef.current.value = '';
  }

  function addLink() {
    const trimmed = url.trim();
    if (trimmed === '') return setUrlError('Cole o link do arquivo.');
    // A conferência séria é no servidor, que só aceita Drive, OneDrive e Dropbox. Aqui é só para o
    // operador não esperar uma viagem de ida e volta por um erro de digitação.
    if (!/^https:\/\//iu.test(trimmed)) return setUrlError('O link precisa começar com https://.');
    setUrlError(undefined);
    setUrl('');
    onAdd([{ kind: 'link', url: trimmed }]);
    return undefined;
  }

  return (
    <fieldset disabled={disabled}>
      <legend className="mb-2 text-sm font-medium">{label}</legend>

      <div
        role="group"
        aria-label="Adicionar comprovante"
        className={`rounded-xl border border-dashed px-4 py-5 text-center transition ${
          dragging
            ? 'border-brand-500/60 bg-brand-500/10'
            : 'border-surface-border bg-surface-elevated'
        }`}
        onDrop={(event: DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          setDragging(false);
          addFiles(event.dataTransfer.files);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
      >
        <Upload className="mx-auto mb-2 size-5 text-brand-400" />
        <div className="flex flex-col justify-center gap-2 sm:flex-row">
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
            Escolher arquivo
          </Button>
          {/* O comprovante nasce em papel na mão de quem comprou. Sem isto o caminho era sair do
              sistema, fotografar pelo aplicativo do aparelho e voltar para procurar o arquivo. */}
          {cameraAvailable() ? (
            <Button type="button" variant="outline" onClick={() => setCameraOpen(true)}>
              <Camera className="size-4" />
              Tirar foto
            </Button>
          ) : null}
          {/* No celular não existe Ctrl+V, e é no celular que se fotografa o comprovante. */}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void readImagesFromClipboard().then((result) => {
                setPasteError(result.ok ? undefined : result.detail);
                if (result.ok) onAdd(result.files.map((file) => ({ kind: 'file', file })));
              });
            }}
          >
            <ClipboardPaste className="size-4" />
            Colar print
          </Button>
        </div>
        <p className="mt-2 text-xs text-content-muted">
          Ou arraste para cá, ou cole com {pasteShortcut()}. PDF, JPG, PNG ou WebP, até 10 MB.
        </p>
        {pasteError ? (
          <p role="alert" className="mt-2 text-xs text-warning">
            {pasteError}
          </p>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED}
          className="sr-only"
          aria-label="Arquivo do comprovante"
          onChange={(event) => addFiles(event.target.files)}
        />
      </div>

      <CameraCapture
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        title="Fotografar comprovante"
        fileName={nextPhotoName(pending.map(describePending))}
        onCapture={(file) => onAdd([{ kind: 'file', file }])}
      />

      <div className="mt-3">
        <Input
          label="Link do arquivo"
          autoComplete="off"
          hint="Google Drive, OneDrive ou Dropbox, compartilhado com quem tem o link."
          value={url}
          error={urlError}
          onChange={(event) => setUrl(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // Enter aqui não pode salvar o lançamento inteiro sem querer.
              event.preventDefault();
              addLink();
            }
          }}
          action={
            <>
              <PasteButton
                onPaste={(text) => {
                  setUrlError(undefined);
                  setUrl(text);
                }}
                onError={setUrlError}
              />
              <Button type="button" variant="outline" onClick={addLink}>
                <Link2 className="size-4" />
                Adicionar link
              </Button>
            </>
          }
        />
      </div>

      {pending.length > 0 ? (
        <ul
          aria-label="Comprovantes a anexar"
          className="mt-3 divide-y divide-surface-border rounded-xl border border-surface-border"
        >
          {pending.map((item, index) => (
            <li
              key={`${item.kind}:${describePending(item)}:${String(index)}`}
              className="flex items-center gap-3 px-4 py-2.5"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-500/10 text-brand-400">
                {item.kind === 'link' ? (
                  <Link2 className="size-4" />
                ) : item.file.type.startsWith('image/') ? (
                  <ImageIcon className="size-4" />
                ) : (
                  <FileText className="size-4" />
                )}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm">{describePending(item)}</span>
              <IconButton
                label={`Retirar ${describePending(item)}`}
                icon={<Trash2 />}
                tone="danger"
                onClick={() => onRemove(index)}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </fieldset>
  );
}
